import { result } from 'lodash-es'
import { useFormContext } from 'react-hook-form'
import { LuRotateCw } from 'react-icons/lu'

import { GEO_FEATURE_MARKER_COLOR, GEOFENCE_FIELDS, GeoMode } from '@/feature/observatory'
import { ColorPicker } from '@/feature/tags/components/ColorPicker'

import { CircleGeoFenceShape } from '../../../shapes/Circle'
import { LineStringGeoFenceShape } from '../../../shapes/Linestring'
import { PolygonGeoFenceShape } from '../../../shapes/Polygon'
import styles from '../../GeoFenceCreateEdit.module.css'
import { NewGeoFenceShapeFieldProps } from '../../GeoFenceCreateEdit.types'
import { getFieldError, shouldShowError } from '../../GeoFenceCreateEdit.utils'

export const NewGeoFenceShapeField = ({
  simulatedPosition,
  setSimulatedPosition,
  geoFence,
}: NewGeoFenceShapeFieldProps) => {
  const { register, getFieldState, watch, setValue } = useFormContext()

  const mode = watch(GEOFENCE_FIELDS.type)

  const renders = {
    [GeoMode.circle]: () => (
      <CircleGeoFenceShape
        simulatedPosition={simulatedPosition}
        setSimulatedPosition={setSimulatedPosition}
      />
    ),
    [GeoMode.polygon]: () => (
      <PolygonGeoFenceShape
        simulatedPosition={simulatedPosition}
        setSimulatedPosition={setSimulatedPosition}
        isEditing={!!geoFence}
      />
    ),
    [GeoMode.linestring]: () => <LineStringGeoFenceShape geoFence={geoFence} />,
  }

  // Determine available modes based on current mode
  const isEditingLineString = mode === GeoMode.linestring
  const availableModes = isEditingLineString
    ? [GeoMode.linestring]
    : [GeoMode.circle, GeoMode.polygon]

  return (
    <div className={styles.geoEditor}>
      <div className={styles.geoFenceShape}>Geofence Type</div>
      <div className={styles.geoFenceShapeOptions}>
        {availableModes.map((v) => (
          <label key={v}>
            <input
              {...register(GEOFENCE_FIELDS.type)}
              type="radio"
              value={v}
              data-testid={`geofence-type-${v}`}
              disabled={isEditingLineString} // Disable changing from linestring
            />
            {v}
          </label>
        ))}

        {/* Only show reset button for non-linestring geofences */}
        {!isEditingLineString && (
          <button
            type="button"
            onClick={() => {
              setValue(GEOFENCE_FIELDS.type, GeoMode.circle)
              setValue(GEOFENCE_FIELDS.points, [])
            }}
            data-testid="geofence-shape-reset"
          >
            <LuRotateCw />
            Reset
          </button>
        )}
      </div>
      {result(renders, mode, null)}
      <div
        className={[
          styles.positionBox,
          shouldShowError(getFieldState(GEOFENCE_FIELDS.address)) ||
          shouldShowError(getFieldState(GEOFENCE_FIELDS.points))
            ? styles.error
            : '',
        ].join(' ')}
      >
        <h3 className={styles.geoFencePosition}>Address</h3>
        {simulatedPosition?.location?.lat.toFixed(6) ?? null},{' '}
        {simulatedPosition?.location?.lng.toFixed(6) ?? null}
        <p>{!simulatedPosition?.address ? 'Loading...' : simulatedPosition?.address}</p>
      </div>
      <label
        className={styles.textInput}
        style={mode === GeoMode.circle || mode === GeoMode.linestring ? {} : { display: 'none' }}
      >
        Radius
        <div className={styles.numberInput}>
          <input
            type="number"
            data-testid="geofence-radius-input"
            {...register(GEOFENCE_FIELDS.radius, {
              valueAsNumber: true,
            })}
            className={[
              styles.input,
              shouldShowError(getFieldState(GEOFENCE_FIELDS.radius)) ? styles.error : '',
            ].join(' ')}
          />
          <div className={styles.radiusUnit}>m</div>
        </div>
        <div className={styles.errorLine}>
          {getFieldError(getFieldState(GEOFENCE_FIELDS.radius))}
        </div>
      </label>
      {mode === GeoMode.linestring && !watch(GEOFENCE_FIELDS.highRisk) ? (
        <ColorPicker
          value={watch(GEOFENCE_FIELDS.color) || GEO_FEATURE_MARKER_COLOR}
          onChange={(color: string) => setValue(GEOFENCE_FIELDS.color, color)}
          dataTestId="linestring-color-picker"
        />
      ) : null}
      {mode === GeoMode.polygon ? (
        <div className={styles.errorLine}>
          {getFieldError(getFieldState(GEOFENCE_FIELDS.points))}
        </div>
      ) : null}
    </div>
  )
}
